import { CreativeOSFeatures } from '@/_shared/subscriptionPlans'
import { CustomLightBulb } from '@/components/shadcn/customLightBulb.tsx'
import { featureFlagKeys } from '@/utils/data/featureFlags'
import { Layout, Star, Swatches } from '@mynaui/icons-react'
import { useNavigate } from '@tanstack/react-router'
import { useFeatureFlagEnabled } from 'posthog-js/react'
import { useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import { useLocalStorage } from 'usehooks-ts'
import AccessControl from './AccessControl'
import SearchBarAndDialog from './custom-components/SearchBarAndDialog'

export default function TemplatesDashboard() {
  const welcomeScreenFlagEnabled = useFeatureFlagEnabled(featureFlagKeys.welcomeScreen)
  const { ref: scrollRef, inView } = useInView({
    threshold: [0.45, 0.55],
    delay: 300,
    initialInView: true,
    rootMargin: '-60px',
  })

  const [welcomeSearchVisible, setWelcomeSearchVisible] = useLocalStorage<boolean>('welcome_search_visible', true)

  const navigate = useNavigate()

  const handleScrollToTemplates = () => {
    const el = document.getElementById('templates')
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' })
    }
  }
  useEffect(() => {
    // only if you truly crossed 50%
    if (inView === true && welcomeSearchVisible === false) {
      setWelcomeSearchVisible(true)
    }
    if (inView === false && welcomeSearchVisible === true) {
      setWelcomeSearchVisible(false)
    }
  }, [inView, welcomeSearchVisible])

  return (
    <div style={{ display: welcomeScreenFlagEnabled ? 'block' : 'none' }}>
      <div
        style={{
          background: `
              linear-gradient(90deg, #A78BFA 0%, #FB923C 50%, #F43F5E 100%)
            `,
        }}
        className="hidden pt-11 pb-14 px-11 mb-5 space-y-8 text-center rounded-xl lg:flex flex-col items-center relative"
      >
        <div>
          <h4 className="text-white font-semibold text-[2.75rem]">Where would you like to start?</h4>
        </div>
        <div className="grid grid-cols-4 gap-6 w-3/4">
          <AccessControl
            feature={CreativeOSFeatures.AD_TEMPLATE_NAV_BUTTON}
            FeatureLocked={
              <div className="cursor-pointer rounded-2xl p-6 flex flex-col items-center bg-white space-y-3">
                <div className="rounded-full w-16 h-16 border-4 border-violet-100 bg-white p-3.5 flex justify-center items-center">
                  <span>
                    <Layout className="dark:text-black" />
                  </span>
                </div>
                <h5 className="font-semibold text-sm text-themedestructive">
                  <strong>Upgrade</strong> To Access The Template Library
                </h5>
              </div>
            }
            FeatureUnlocked={
              <div
                onClick={() => {
                  handleScrollToTemplates()
                }}
                className="cursor-pointer rounded-2xl p-6 flex flex-col items-center bg-white space-y-3"
              >
                <div className="rounded-full w-16 h-16 border-4 border-violet-100 bg-white p-3.5 flex justify-center items-center">
                  <span>
                    <Layout className="dark:text-black" />
                  </span>
                </div>

                <div className="text-sm">
                  <h5 className="font-medium text-thememutedforeground">I want to</h5>
                  <h5 className={`font-semibold dark:text-black`}>Browse Templates</h5>
                </div>
              </div>
            }
          />
          <div
            onClick={() => {
              navigate({
                to: '/feeds/inspiration',
              })
            }}
            className="cursor-pointer rounded-2xl p-6 flex flex-col items-center bg-white space-y-3"
          >
            <div className="rounded-full w-16 h-16 border-4 border-violet-100 bg-white p-3.5 flex justify-center items-center">
              <span className="dark:text-black">
                <CustomLightBulb />
              </span>
            </div>
            <div className="text-sm">
              <h5 className="font-medium text-thememutedforeground">I want to</h5>
              <h5 className="font-semibold dark:text-black">Be Inspired</h5>
            </div>
          </div>
          <div
            onClick={() => {
              navigate({
                to: '/feeds/boards',
              })
            }}
            className="cursor-pointer rounded-2xl p-6 flex flex-col items-center bg-white space-y-3"
          >
            <div className="rounded-full w-16 h-16 border-4 border-violet-100 bg-white p-3.5 flex justify-center items-center">
              <span>
                <Star className="dark:text-black" />
              </span>
            </div>
            <div className="text-sm">
              <h5 className="font-medium text-thememutedforeground">I want to</h5>
              <h5 className="font-semibold dark:text-black">View My Collections</h5>
            </div>
          </div>

          <AccessControl
            feature={CreativeOSFeatures.USE_CREATIVE_TOOLKIT}
            FeatureLocked={
              <div className="cursor-pointer rounded-2xl p-6 flex flex-col items-center bg-white space-y-3">
                <div className="rounded-full w-16 h-16 border-4 border-violet-100 bg-white p-3.5 flex justify-center items-center">
                  <span>
                    <Swatches className="dark:text-black" />
                  </span>
                </div>
                <h5 className="font-semibold text-sm text-themedestructive">
                  <strong>Upgrade</strong> To More Creative Power
                </h5>
              </div>
            }
            FeatureUnlocked={
              <div
                onClick={() => {
                  navigate({
                    to: '/feeds/creative-toolkit',
                  })
                }}
                className="cursor-pointer rounded-2xl p-6 flex flex-col items-center bg-white space-y-3"
              >
                <div className="rounded-full w-16 h-16 border-4 border-violet-100 bg-white p-3.5 flex justify-center items-center">
                  <span>
                    <Swatches className="dark:text-black" />
                  </span>
                </div>
                <div className="text-sm">
                  <h5 className="font-medium text-thememutedforeground">I want to</h5>
                  <h5 className="font-semibold dark:text-black">Use Creative Toolkit</h5>
                </div>
              </div>
            }
          />
        </div>
        <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 w-full flex justify-center" ref={scrollRef}>
          <SearchBarAndDialog />
        </div>
      </div>
    </div>
  )
}
